import { AccountRefusedError } from '@jbroll/rowboat-client';
import type { PhotoRow } from '@/schema/estate';
import { readMedia } from './photoService';

export type UploadState = 'waiting' | 'uploading' | 'failed';
/** `checking`: this device's queue has not been listed yet, so absence means nothing. */
export type QueueState = UploadState | 'checking';
export type PhotoState = QueueState | 'uploaded' | 'missing';
export type UploadStatus = Readonly<Record<string, UploadState>>;

export function enqueued(s: UploadStatus, key: string): UploadStatus {
  return { ...s, [key]: 'waiting' };
}

export function listed(s: UploadStatus, keys: readonly string[]): UploadStatus {
  const next: Record<string, UploadState> = {};
  for (const key of keys) next[key] = s[key] ?? 'waiting';
  return next;
}

const failUploading = (s: UploadStatus): Record<string, UploadState> =>
  Object.fromEntries(Object.entries(s).map(([k, v]) => [k, v === 'uploading' ? 'failed' : v]));

// Drains run one at a time and entries upload in sequence, so a newer start means the
// previous upload did not commit.
export function started(s: UploadStatus, key: string): UploadStatus {
  return { ...failUploading(s), [key]: 'uploading' };
}

export function committed(s: UploadStatus, key: string): UploadStatus {
  return dropped(s, [key]);
}

export function drained(s: UploadStatus, failedKeys: readonly string[]): UploadStatus {
  const next = failUploading(s);
  for (const key of failedKeys) next[key] = 'failed';
  return next;
}

export function dropped(s: UploadStatus, keys: readonly string[]): UploadStatus {
  return Object.fromEntries(Object.entries(s).filter(([k]) => !keys.includes(k)));
}

const RETRY_FIRST_MS = 5_000;
const RETRY_CEILING_MS = 300_000;

/** The wait before automatic retry `attempt` (from 0), jittered so tabs and phones spread out. */
export function retryDelayMs(attempt: number, random: () => number = Math.random): number {
  const ceiling = Math.min(RETRY_FIRST_MS * 2 ** attempt, RETRY_CEILING_MS);
  return ceiling / 2 + random() * (ceiling / 2);
}

// The rowboat client reports an upload's HTTP status only in its message (client/src/files.ts).
const UPLOAD_FAILED = /^upload failed: (\d{3})$/;

/** False for a failure the same request would hit again: a refusal or a 4xx but 408 and 429. */
export function canRetry(error: Error): boolean {
  if (error instanceof AccountRefusedError) return false;
  const status = Number(UPLOAD_FAILED.exec(error.message)?.[1]);
  return !(status >= 400 && status < 500 && status !== 408 && status !== 429);
}

export const pendingCount = (s: UploadStatus): number => Object.keys(s).length;

export const failedCount = (s: UploadStatus): number =>
  Object.values(s).filter((v) => v === 'failed').length;

export function photoState(
  photo: Pick<PhotoRow, 'storage_key'>,
  upload: QueueState | undefined,
): PhotoState {
  if (readMedia(photo.storage_key)) return 'uploaded';
  return upload ?? 'missing';
}
